import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AddressSchema, type AddressDto } from '@rozman/shared';
import { z } from 'zod';
import { api, ApiError, cartApi } from '@/lib/api';
import { useCart } from '@/lib/cart-store';
import { useLocale, useLocalePath } from '@/hooks/useLocalePath';
import { Price } from '@/components/Price';
import { Seo } from '@/components/Seo';
import type { JSX, ReactNode } from 'react';

const FormSchema = AddressSchema.extend({
  email: z.string().email(),
  wantsInvoice: z.boolean().default(false),
  paymentMethod: z.enum(['blik', 'card', 'transfer']),
  acceptsTerms: z.literal(true),
  acceptsWithdrawalInfo: z.literal(true),
  marketingConsent: z.boolean().default(false),
});
type FormValues = z.infer<typeof FormSchema>;

interface CheckoutResult {
  orderId: string;
  number: string;
  status: string;
  paymentStatus: string;
  totalGross: number;
  invoiceNumber: string | null;
  supplierOrderNumbers: string[];
}

/** Three steps: contact, delivery address, payment. Guest checkout allowed throughout. */
export function CheckoutPage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const path = useLocalePath();
  const navigate = useNavigate();
  const { items, promoCode, clear } = useCart();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: quote } = useQuery({
    queryKey: ['cart', items, promoCode, locale],
    queryFn: () => cartApi.quote(items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })), locale, promoCode),
    enabled: items.length > 0,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { countryCode: 'PL', paymentMethod: 'blik', wantsInvoice: false, marketingConsent: false },
  });

  if (items.length === 0) {
    return (
      <>
        {/* Every page emits its canonical and hreflang pair, empty states included. */}
        <Seo title={t('checkout.title')} path="/zamowienie" noindex />
        <div className="rz-shell py-32 text-center space-y-4">
          <h1 className="font-display text-xl">{t('cart.empty')}</h1>
          <Link to={path('/')} className="rz-button">{t('cart.emptyCta')}</Link>
        </div>
      </>
    );
  }

  const onSubmit = async (values: FormValues): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    const { email, wantsInvoice, paymentMethod, acceptsTerms, acceptsWithdrawalInfo, marketingConsent, ...address } =
      values;
    try {
      const result = await api<CheckoutResult>('/orders/checkout', {
        method: 'POST',
        body: JSON.stringify({
          locale,
          email,
          items: items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })),
          shippingAddress: address as AddressDto,
          wantsInvoice,
          paymentMethod,
          promoCode,
          acceptsTerms,
          acceptsWithdrawalInfo,
          marketingConsent,
        }),
      });
      clear();
      navigate(path(`/zamowienie/${result.number}`), { state: result });
    } catch (error) {
      // The error code is an i18n key, so a Ukrainian customer gets a Ukrainian message.
      setSubmitError(error instanceof ApiError ? error.code : 'errors.generic');
    } finally {
      setSubmitting(false);
    }
  };

  const next = async (fields: Array<keyof FormValues>): Promise<void> => {
    const ok = await trigger(fields);
    if (ok) setStep((s) => (s === 1 ? 2 : 3));
  };

  const err = (field: keyof FormValues): string | undefined => {
    const message = errors[field]?.message;
    return message ? t(`validation.${String(message)}`, { defaultValue: t('validation.required') }) : undefined;
  };

  return (
    <>
      <Seo title={t('checkout.title')} path="/zamowienie" noindex />

      <div className="rz-shell grid gap-12 py-10 lg:grid-cols-[1fr_360px] pb-24">
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-8" data-testid="checkout-form">
          <h1 className="text-xl md:text-2xl">{t('checkout.title')}</h1>

          <ol className="flex gap-6 text-xs">
            {([1, 2, 3] as const).map((n) => (
              <li key={n} className="flex items-center gap-2" aria-current={step === n ? 'step' : undefined}>
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full text-2xs font-bold"
                  style={{
                    background: step >= n ? 'var(--color-ink)' : 'var(--color-ground-deep)',
                    color: step >= n ? 'var(--color-ground)' : 'var(--color-muted)',
                  }}
                >
                  {n}
                </span>
                <span className={step === n ? 'font-semibold' : 'text-[var(--color-muted)]'}>
                  {t(`checkout.step${n}`)}
                </span>
              </li>
            ))}
          </ol>

          {step === 1 && (
            <fieldset className="space-y-4">
              <legend className="sr-only">{t('checkout.step1')}</legend>
              <Field label={t('checkout.email')} error={err('email')}>
                <input {...register('email')} type="email" autoComplete="email" className="rz-field" aria-invalid={!!errors.email} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('checkout.firstName')} error={err('firstName')}>
                  <input {...register('firstName')} autoComplete="given-name" className="rz-field" aria-invalid={!!errors.firstName} />
                </Field>
                <Field label={t('checkout.lastName')} error={err('lastName')}>
                  <input {...register('lastName')} autoComplete="family-name" className="rz-field" aria-invalid={!!errors.lastName} />
                </Field>
              </div>
              <Field label={t('checkout.phone')} error={err('phone')}>
                <input {...register('phone')} type="tel" autoComplete="tel" className="rz-field" aria-invalid={!!errors.phone} />
              </Field>
              <p className="text-2xs text-[var(--color-muted)]">{t('checkout.guestCheckout')}</p>
              <button type="button" onClick={() => void next(['email', 'firstName', 'lastName', 'phone'])} className="rz-button">
                {t('common.next')}
              </button>
            </fieldset>
          )}

          {step === 2 && (
            <fieldset className="space-y-4">
              <legend className="sr-only">{t('checkout.step2')}</legend>
              <Field label={t('checkout.street')} error={err('street')}>
                <input {...register('street')} autoComplete="address-line1" className="rz-field" aria-invalid={!!errors.street} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('checkout.buildingNumber')} error={err('buildingNumber')}>
                  <input {...register('buildingNumber')} className="rz-field" aria-invalid={!!errors.buildingNumber} />
                </Field>
                <Field label={`${t('checkout.apartmentNumber')} (${t('common.optional')})`}>
                  <input {...register('apartmentNumber')} className="rz-field" />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
                <Field label={t('checkout.postalCode')} error={err('postalCode')}>
                  <input {...register('postalCode')} placeholder="00-000" autoComplete="postal-code" className="rz-field" aria-invalid={!!errors.postalCode} />
                </Field>
                <Field label={t('checkout.city')} error={err('city')}>
                  <input {...register('city')} autoComplete="address-level2" className="rz-field" aria-invalid={!!errors.city} />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" {...register('wantsInvoice')} />
                {t('checkout.wantsInvoice')}
              </label>
              <Field label={`${t('checkout.nip')} (${t('common.optional')})`} hint={t('checkout.nipHelp')} error={err('nip')}>
                <input {...register('nip')} inputMode="numeric" className="rz-field" aria-invalid={!!errors.nip} />
              </Field>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="rz-button rz-button--ghost">{t('common.back')}</button>
                <button type="button" onClick={() => void next(['street', 'buildingNumber', 'postalCode', 'city'])} className="rz-button">
                  {t('common.next')}
                </button>
              </div>
            </fieldset>
          )}

          {step === 3 && (
            <fieldset className="space-y-5">
              <legend className="sr-only">{t('checkout.step3')}</legend>

              {/* Stated plainly, before payment, on the page itself. */}
              <p className="bg-[var(--color-accent)]/10 border-l-2 border-[var(--color-accent)] p-3 text-xs leading-relaxed" data-testid="mock-payment-notice">
                {t('checkout.mockPaymentBanner')}
              </p>

              <div className="space-y-2">
                {(['blik', 'card', 'transfer'] as const).map((method) => (
                  <label key={method} className="flex items-center gap-3 border border-[var(--color-line)] p-3 text-sm cursor-pointer">
                    <input type="radio" value={method} {...register('paymentMethod')} />
                    {t(`checkout.${method}`)}
                  </label>
                ))}
              </div>

              {/* Pre-contractual information, visible before the order is confirmed. */}
              <section className="space-y-1.5 bg-[var(--color-ground-lift)] p-4 text-2xs leading-relaxed">
                <p className="rz-eyebrow">{t('checkout.preContractual')}</p>
                <p>{t('checkout.sellerIdentity')}: Rozman sp. z o.o., ul. Meblowa 12, 00-001 Warszawa, NIP 5252525252</p>
                <p>
                  {t('checkout.totalWithVat')}: <Price minor={quote?.totalGross ?? null} className="font-semibold" />
                </p>
                <p>
                  {t('checkout.deliveryTime')}:{' '}
                  {quote?.shipments.map((s) => `${s.supplierName} ${s.deliveryEstimateDays[0]}–${s.deliveryEstimateDays[1]} d`).join(' · ')}
                </p>
                <p>
                  {t('checkout.complaintProcedure')}:{' '}
                  <Link to={path('/strona/regulamin')} className="underline underline-offset-2">{t('legal.terms')}</Link>
                </p>
              </section>

              <div className="space-y-2 text-xs">
                <label className="flex items-start gap-2">
                  <input type="checkbox" {...register('acceptsTerms')} className="mt-0.5" />
                  <span>
                    <Link to={path('/strona/regulamin')} className="underline underline-offset-2">{t('legal.terms')}</Link>
                    {' · '}
                    <Link to={path('/strona/polityka-prywatnosci')} className="underline underline-offset-2">{t('legal.privacy')}</Link>
                  </span>
                </label>
                {errors.acceptsTerms && <p className="text-2xs text-[var(--color-danger)]">{t('validation.acceptTermsRequired')}</p>}

                <label className="flex items-start gap-2">
                  <input type="checkbox" {...register('acceptsWithdrawalInfo')} className="mt-0.5" />
                  <span>
                    <Link to={path('/strona/prawo-odstapienia')} className="underline underline-offset-2">
                      {t('returns.withdrawalPeriod')}
                    </Link>
                  </span>
                </label>
                {errors.acceptsWithdrawalInfo && (
                  <p className="text-2xs text-[var(--color-danger)]">{t('validation.acceptWithdrawalRequired')}</p>
                )}

                <label className="flex items-start gap-2 text-[var(--color-muted)]">
                  <input type="checkbox" {...register('marketingConsent')} className="mt-0.5" />
                  {t('checkout.marketingConsent')}
                </label>
              </div>

              {Object.keys(errors).length > 0 && (
                <div role="alert" className="border-l-2 border-[var(--color-danger)] bg-[var(--color-danger)]/8 p-3 text-xs">
                  <p className="font-semibold">{t('errors.generic')}</p>
                  <ul className="mt-1 space-y-0.5">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field}>
                        {t(`checkout.${field}`, { defaultValue: field })}:{' '}
                        {t(`validation.${String(error?.message ?? 'required')}`, { defaultValue: t('validation.required') })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {submitError && (
                <div role="alert" className="border-l-2 border-[var(--color-danger)] bg-[var(--color-danger)]/8 p-3 text-xs">
                  <p className="font-semibold">{t(submitError, { defaultValue: t('errors.generic') })}</p>
                  {submitError === 'checkout.paymentFailed' && <p className="mt-1">{t('checkout.paymentFailedBody')}</p>}
                </div>
              )}

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="rz-button rz-button--ghost">{t('common.back')}</button>
                <button type="submit" disabled={submitting} className="rz-button flex-1" data-testid="place-order">
                  {submitting ? t('checkout.processing') : t('checkout.placeOrder')}
                </button>
              </div>
            </fieldset>
          )}
        </form>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <h2 className="rz-eyebrow">{t('checkout.summary')}</h2>
          <ul className="space-y-3">
            {(quote?.lines ?? []).map((line) => (
              <li key={line.variantId} className="flex gap-3">
                {line.image && <img src={line.image} alt="" width={48} height={60} className="h-15 w-12 shrink-0 bg-[var(--color-ground-deep)] object-contain" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold leading-tight">{line.name}</p>
                  <p className="text-2xs text-[var(--color-muted)]">× {line.quantity}</p>
                </div>
                <Price minor={line.lineTotalGross} className="text-xs shrink-0" />
              </li>
            ))}
          </ul>
          <dl className="space-y-1.5 border-t border-[var(--color-line)] pt-3 text-xs">
            {(quote?.shipments ?? []).map((s) => (
              <div key={s.supplierCode} className="flex justify-between" data-testid="checkout-shipment">
                <dt className="text-[var(--color-muted)]">{t('cart.shipmentFrom', { supplier: s.supplierName })}</dt>
                <dd><Price minor={s.totalCost} /></dd>
              </div>
            ))}
            <div className="flex justify-between pt-2 text-base font-semibold">
              <dt>{t('checkout.totalWithVat')}</dt>
              <dd><Price minor={quote?.totalGross ?? null} /></dd>
            </div>
          </dl>
        </aside>
      </div>
    </>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold">{label}</span>
      {children}
      {hint && !error && <span className="block text-2xs text-[var(--color-muted)]">{hint}</span>}
      {error && <span role="alert" className="block text-2xs text-[var(--color-danger)]">{error}</span>}
    </label>
  );
}
